import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users, Car, Wrench, ShieldCheck, Sparkles, ArrowRight,
  AlertCircle, Phone, FileText, Activity, ChevronRight, Clock,
} from "lucide-react";
import api from "@/lib/api";
import AdminCockpit from "@/components/dashboard/AdminCockpit";
import RaccourcisRapides from "@/components/dashboard/RaccourcisRapides";

const TYPE_LABELS = {
  rdv: { label: "Rendez-vous", icon: Clock, color: "text-blue-600 bg-blue-50" },
  intervention: { label: "Intervention", icon: Wrench, color: "text-orange-600 bg-orange-50" },
  pret: { label: "Prêt véh.", icon: Car, color: "text-purple-600 bg-purple-50" },
  facture: { label: "Facture", icon: FileText, color: "text-pink-600 bg-pink-50" },
  paiement: { label: "Paiement", icon: ShieldCheck, color: "text-green-600 bg-green-50" },
  appel: { label: "Appel", icon: Phone, color: "text-cyan-600 bg-cyan-50" },
  note: { label: "Note", icon: FileText, color: "text-gray-600 bg-gray-50" },
  systeme: { label: "Système", icon: Activity, color: "text-gray-500 bg-gray-50" },
};

function StatCard({ label, value, icon: Icon, accent, testId }) {
  return (
    <Card className="p-5 hover:shadow-md transition-shadow border-gray-200" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">{label}</div>
          <div className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mt-2">{value}</div>
        </div>
        <div className={`w-10 h-10 rounded-md ${accent} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [aRelancer, setARelancer] = useState([]);
  const [activite, setActivite] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, r, a] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/dashboard/a-relancer"),
          api.get("/dashboard/activite-recente?limit=15"),
        ]);
        setStats(s.data);
        setARelancer(r.data);
        setActivite(a.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const today = new Date();
  const greeting = today.getHours() < 12 ? "Bonjour" : today.getHours() < 18 ? "Bon après-midi" : "Bonsoir";

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900">
          {greeting}, {user?.nom?.split(" ")[0]}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {today.toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </p>
      </div>

      {/* iter326 — Raccourcis, avant tout le reste : c'est le premier geste sur mobile */}
      <RaccourcisRapides />

      {/* iter166 — Cockpit admin (vue direction) */}
      {user?.role === "admin" && <AdminCockpit />}

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Clients actifs"
          value={loading ? "—" : stats?.clients_actifs ?? 0}
          icon={Users}
          accent="bg-blue-50 text-blue-600"
          testId="stat-clients-actifs"
        />
        <StatCard
          label="Véhicules en parc"
          value={loading ? "—" : stats?.vehicules_total ?? 0}
          icon={Car}
          accent="bg-purple-50 text-purple-600"
          testId="stat-vehicules-total"
        />
        <StatCard
          label="À relancer (&gt;1 an)"
          value={loading ? "—" : stats?.a_relancer ?? 0}
          icon={Wrench}
          accent="bg-orange-50 text-orange-600"
          testId="stat-a-relancer"
        />
        <StatCard
          label="Abonnés CarMaintenance"
          value={loading ? "—" : stats?.clients_abonnes ?? 0}
          icon={Sparkles}
          accent="bg-[#52a675]/10 text-[#52a675]"
          testId="stat-clients-abonnes"
        />
      </div>

      {/* 2-column main blocks */}
      <div className="grid lg:grid-cols-2 gap-6 min-w-0">
        {/* À relancer */}
        <Card className="border-gray-200 min-w-0 overflow-hidden" data-testid="block-a-relancer">
          <div className="p-4 sm:p-5 border-b border-gray-100 flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-md bg-orange-50 text-orange-600 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <h2 className="font-heading font-semibold text-gray-900 text-sm sm:text-base truncate">À relancer cette semaine</h2>
                <p className="text-xs text-gray-500 truncate">Clients sans facture depuis &gt; 1 an</p>
              </div>
            </div>
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
              {aRelancer.length}
            </Badge>
          </div>
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {aRelancer.length === 0 && !loading && (
              <div className="p-8 text-center text-sm text-gray-400">
                Aucune relance à faire — tous tes clients sont à jour 👍
              </div>
            )}
            {aRelancer.slice(0, 8).map((it) => (
              <button
                key={it.client_id}
                onClick={() => navigate(`/clients/${it.client_id}`)}
                className="w-full text-left p-3 sm:p-4 hover:bg-gray-50 flex items-center gap-2 sm:gap-3 transition-colors"
                data-testid={`relance-item-${it.client_id}`}
              >
                <div className="w-9 h-9 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Car className="w-4 h-4 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-900 truncate">
                    {it.client_nom}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {it.vehicule_principal
                      ? `${it.vehicule_principal.plaque || ""} · ${it.vehicule_principal.marque || ""} ${it.vehicule_principal.modele || ""}`
                      : (it.client_tel || it.client_email || "—")}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-[10px]">
                    {it.jours_depuis != null ? `${it.jours_depuis} j` : "—"}
                  </Badge>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        </Card>

        {/* Activité récente */}
        <Card className="border-gray-200 min-w-0 overflow-hidden" data-testid="block-activite-recente">
          <div className="p-4 sm:p-5 border-b border-gray-100 flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                <Activity className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <h2 className="font-heading font-semibold text-gray-900 text-sm sm:text-base truncate">Activité récente</h2>
                <p className="text-xs text-gray-500 truncate">Dernières actions sur l'application</p>
              </div>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {activite.length === 0 && !loading && (
              <div className="p-8 text-center text-sm text-gray-400">Aucune activité récente</div>
            )}
            <ul className="p-4 space-y-3">
              {activite.map((h) => {
                const meta = TYPE_LABELS[h.type] || TYPE_LABELS.systeme;
                const Icon = meta.icon;
                const date = new Date(h.date);
                return (
                  <li
                    key={h.id}
                    className="flex items-start gap-3 cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1.5 rounded-md"
                    onClick={() => navigate(h.links?.[0]?.url || `/clients/${h.client_id}`)}
                    data-testid={`activite-item-${h.id}`}
                  >
                    <div className={`w-7 h-7 rounded-full ${meta.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-900 leading-tight">
                        <span className="font-semibold">{h.client_nom}</span> — {h.description}
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        {date.toLocaleString("fr-CH", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        {h.user_nom && ` · ${h.user_nom}`}
                        {h.links?.[0] && ` · ${h.links[0].label}`}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </Card>
      </div>

      {/* Quick actions */}
      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          onClick={() => navigate("/clients/nouveau")}
          className="bg-[#52a675] hover:bg-[#3d8a5c]"
          data-testid="quick-action-new-client"
        >
          <Users className="w-4 h-4 mr-2" />
          Nouveau client
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate("/clients")}
          data-testid="quick-action-list-clients"
        >
          Voir tous les clients
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
